import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, FileSignature, ArrowRight, AlertTriangle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const CLASS_LABELS = {
  sme_commercial: 'SME Commercial', public_liability: 'Public Liability', commercial_motor: 'Commercial Motor',
  professional_indemnity: 'Professional Indemnity', cyber: 'Cyber', residential_landlords: 'Residential Landlords',
  commercial_landlords: 'Commercial Landlords', strata: 'Strata', farm: 'Farm', home_contents: 'Home & Contents',
  personal_motor: 'Personal Motor', construction: 'Construction', trade_credit: 'Trade Credit',
};
const STATUS = { pending: { c: C.amber, Icon: Clock, label: 'Pending' }, approved: { c: C.green, Icon: CheckCircle2, label: 'Approved' }, rejected: { c: C.red, Icon: XCircle, label: 'Rejected' } };
const fmt$ = (v) => { const n = Number(v || 0); return n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(1)}K` : `$${n.toLocaleString()}`; };
const pretty = (k) => String(k).replace(/\./g, ' › ').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function ActuarialSignOffPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);   // proposal_id expanded
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    return api.get('/rates/proposals')
      .then(r => setData(r.data))
      .catch(e => console.error(e?.message))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const expand = (id) => {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id); setDetail(null); setNote('');
    api.get(`/rates/proposals/${id}`).then(r => setDetail(r.data)).catch(() => {});
  };

  const signoff = (id, decision) => {
    if (decision === 'reject' && !note.trim()) { toast.error('A rejection reason is required'); return; }
    setBusy(true);
    api.post(`/rates/proposals/${id}/signoff`, { decision, note })
      .then(() => { toast.success(decision === 'approve' ? 'Approved & applied live' : 'Proposal rejected'); setOpen(null); setDetail(null); setNote(''); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Sign-off failed'))
      .finally(() => setBusy(false));
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;

  const proposals = data?.proposals || [];
  const pending = proposals.filter(p => p.status === 'pending');
  const resolved = proposals.filter(p => p.status !== 'pending');

  return (
    <div className="space-y-5" data-testid="actuarial-signoff-page">
      <div className="grid grid-cols-3 gap-3">
        {['pending', 'approved', 'rejected'].map(s => {
          const n = proposals.filter(p => p.status === s).length;
          const cfg = STATUS[s];
          return (
            <Card key={s} className="p-4 rounded-md flex items-center gap-3" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={`signoff-count-${s}`}>
              <cfg.Icon className="w-6 h-6" style={{ color: cfg.c }} />
              <div><div className="text-2xl font-bold font-mono" style={{ color: C.navy }}>{n}</div><div className="text-xs capitalize" style={{ color: C.slate }}>{cfg.label}</div></div>
            </Card>
          );
        })}
      </div>

      <section data-testid="pending-queue">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: C.navy }}><FileSignature className="w-4 h-4" style={{ color: C.amber }} />Awaiting Sign-Off ({pending.length})</h3>
        {pending.length === 0 ? (
          <p className="text-sm" style={{ color: C.slate }} data-testid="no-pending">No proposals pending. Rate changes submitted in the editor appear here for review.</p>
        ) : (
          <div className="space-y-3">{pending.map(p => (
            <ProposalCard key={p.proposal_id} p={p} open={open === p.proposal_id} detail={detail} note={note} setNote={setNote} busy={busy} onToggle={() => expand(p.proposal_id)} onSign={signoff} />
          ))}</div>
        )}
      </section>

      {resolved.length > 0 && (
        <section data-testid="resolved-list">
          <h3 className="text-sm font-semibold mb-2" style={{ color: C.navy }}>Decision History</h3>
          <div className="space-y-2">{resolved.map(p => {
            const cfg = STATUS[p.status];
            return (
              <Card key={p.proposal_id} className="px-4 py-3 rounded-md flex items-center justify-between gap-3" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={`resolved-${p.proposal_id}`}>
                <div className="min-w-0">
                  <span className="text-sm font-medium" style={{ color: C.navy }}>{CLASS_LABELS[p.class_id] || p.class_id}</span>
                  <span className="text-xs ml-2" style={{ color: C.slate }}>{p.label} · {p.change_count} change(s)</span>
                  {p.attestation?.note && <p className="text-[11px] mt-0.5 italic" style={{ color: C.slate }}>“{p.attestation.note}”</p>}
                  <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>
                    by {p.attestation?.signer_name || '—'}{p.resulting_version ? ` → v${p.resulting_version}` : ''}{p.attestation?.self_signed ? ' · self-signed' : ''}
                  </p>
                </div>
                <span className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded shrink-0" style={{ background: `${cfg.c}1A`, color: cfg.c }}>
                  <cfg.Icon className="w-3.5 h-3.5" />{cfg.label}
                </span>
              </Card>
            );
          })}</div>
        </section>
      )}
    </div>
  );
}

function ProposalCard({ p, open, detail, note, setNote, busy, onToggle, onSign }) {
  const impact = detail?.impact || p.impact;
  const up = (impact?.gross_delta || 0) > 0;
  return (
    <Card className="rounded-md overflow-hidden" style={{ border: `1px solid ${C.amber}40` }} data-testid={`proposal-${p.proposal_id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`proposal-toggle-${p.proposal_id}`}>
        <div className="min-w-0">
          <span className="text-sm font-semibold" style={{ color: C.navy }}>{CLASS_LABELS[p.class_id] || p.class_id}</span>
          <span className="text-xs ml-2" style={{ color: C.slate }}>{p.label}</span>
          <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{p.change_count} field change(s) · proposed by {p.created_by_name} · {new Date(p.created_at).toLocaleString()}</p>
        </div>
        {impact?.computed && (
          <span className="text-xs font-mono font-semibold shrink-0" style={{ color: up ? C.red : C.green }} data-testid={`proposal-impact-${p.proposal_id}`}>
            {up ? '+' : ''}{impact.gross_delta_pct != null ? `${impact.gross_delta_pct}%` : fmt$(impact.gross_delta)}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`proposal-detail-${p.proposal_id}`}>
          {!detail ? <Skeleton className="h-24 mt-3" /> : (
            <>
              {impact?.computed && (
                <div className="flex items-center gap-3 mt-3 mb-3 text-xs" style={{ color: C.slate }}>
                  <span>Sample gross premium:</span>
                  <span className="font-mono" style={{ color: C.navy }}>{fmt$(impact.gross_before)}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                  <span className="font-mono font-semibold" style={{ color: up ? C.red : C.green }}>{fmt$(impact.gross_after)}</span>
                </div>
              )}
              <div className="rounded-md overflow-hidden border" style={{ borderColor: 'rgba(136,150,168,0.14)' }}>
                <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ background: '#F4F6F9', color: C.slate }}>
                  <span className="col-span-6">Field</span><span className="col-span-2 text-right">From</span><span className="col-span-2 text-right">To</span><span className="col-span-2 text-right">Δ%</span>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {(detail.diff || []).map((d, i) => (
                    <div key={i} className="grid grid-cols-12 px-3 py-1.5 text-xs border-t items-center" style={{ borderColor: 'rgba(136,150,168,0.07)' }} data-testid={`diff-row-${i}`}>
                      <span className="col-span-6 truncate" style={{ color: C.navy }}>{pretty(d.field)}</span>
                      <span className="col-span-2 text-right font-mono" style={{ color: C.slate }}>{String(d.from)}</span>
                      <span className="col-span-2 text-right font-mono font-semibold" style={{ color: C.navy }}>{String(d.to)}</span>
                      <span className="col-span-2 text-right font-mono" style={{ color: d.delta_pct > 0 ? C.red : d.delta_pct < 0 ? C.green : C.slate }}>{d.delta_pct != null ? `${d.delta_pct}%` : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="signoff-note"
                placeholder="Attestation note (required to reject)" className="mt-3 text-xs" rows={2} />
              <div className="flex items-center justify-between mt-3">
                <p className="text-[11px] flex items-center gap-1" style={{ color: C.amber }}><AlertTriangle className="w-3 h-3" />Approving applies the change live immediately.</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => onSign(p.proposal_id, 'reject')} disabled={busy} data-testid="reject-btn"
                    style={{ color: C.red, borderColor: `${C.red}55` }}>Reject</Button>
                  <Button size="sm" onClick={() => onSign(p.proposal_id, 'approve')} disabled={busy} data-testid="approve-btn"
                    style={{ background: C.green, color: 'white' }}>Approve & Apply</Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
